import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Card, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Avatar, Divider, Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import CancelIcon      from '@mui/icons-material/CancelRounded';
import { fetchApprovals, respondApproval } from '../../app/slices/approvalsSlice';
import { PageHeader }     from '../../components/common/PageHeader';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { EmptyState }     from '../../components/common/EmptyState';
import { ApprovalChip }   from '../../components/common/StatusChip';
import { formatDateTime, getInitials } from '../../utils/helpers';

export default function ApprovalsPage() {
  const dispatch = useDispatch();
  const { list, loading } = useSelector(s => s.approvals);
  const [selected, setSelected]   = useState(null);
  const [action, setAction]       = useState('');   // 'Approve' | 'Reject'
  const [comment, setComment]     = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => { dispatch(fetchApprovals()); }, [dispatch]);

  const pending  = list.filter(a => a.status === 'Pending');
  const resolved = list.filter(a => a.status !== 'Pending');

  const openRespond = (item, act) => { setSelected(item); setAction(act); setComment(''); setError(''); };
  const closeDialog = () => { setSelected(null); setAction(''); setComment(''); setError(''); };

  const handleRespond = async () => {
    if (!selected) return;
    setSaving(true); setError('');
    const res = await dispatch(respondApproval({
      id: selected.id,
      dto: { status: action, responseComment: comment },
    }));
    if (res.type.endsWith('/rejected')) setError(res.payload || 'Failed.');
    else closeDialog();
    setSaving(false);
  };

  if (loading && !list.length) return <LoadingSpinner />;

  const ApprovalCard = ({ a }) => (
    <Card sx={{ p: 2.5, mb: 2 }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1.5} mb={1}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#e0e7ff', color: '#4f46e5' }}>
              {getInitials(a.requestedByName)}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>{a.taskTitle}</Typography>
              <Typography variant="caption" color="text.secondary">
                Requested by <strong>{a.requestedByName}</strong> · {formatDateTime(a.requestedAt)}
              </Typography>
            </Box>
          </Box>
          {a.requestComment && (
            <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.05em">
                Work Description
              </Typography>
              <Typography variant="body2" mt={0.5}>{a.requestComment}</Typography>
            </Box>
          )}
          {a.responseComment && (
            <Alert severity={a.status === 'Approved' ? 'success' : 'error'} sx={{ borderRadius: 2 }}>
              <Typography variant="caption" fontWeight={700}>
                {a.status} by {a.respondedByName} · {formatDateTime(a.respondedAt)}
              </Typography>
              <Typography variant="body2">{a.responseComment}</Typography>
            </Alert>
          )}
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
          <ApprovalChip status={a.status} />
          {a.status === 'Pending' && (
            <Box display="flex" gap={1} mt={0.5}>
              <Button size="small" variant="contained" color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => openRespond(a, 'Approve')}
                sx={{ boxShadow: 'none', bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
                Approve
              </Button>
              <Button size="small" variant="contained" color="error"
                startIcon={<CancelIcon />}
                onClick={() => openRespond(a, 'Reject')}
                sx={{ boxShadow: 'none', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                Reject
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );

  return (
    <Box>
      <PageHeader
        title="Approvals"
        subtitle={`${pending.length} pending · ${resolved.length} resolved`}
      />

      {list.length === 0 ? <EmptyState message="No approval requests yet." /> : (
        <>
          {pending.length > 0 && (
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing="0.1em" mb={1.5} display="block">
                Pending ({pending.length})
              </Typography>
              {pending.map(a => <ApprovalCard key={a.id} a={a} />)}
            </Box>
          )}
          {resolved.length > 0 && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing="0.1em" mb={1.5} display="block">
                Resolved ({resolved.length})
              </Typography>
              {resolved.map(a => <ApprovalCard key={a.id} a={a} />)}
            </Box>
          )}
        </>
      )}

      {/* Respond Dialog */}
      <Dialog open={!!selected} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "'Sora',sans-serif", fontWeight: 700,
          color: action === 'Approve' ? '#059669' : '#dc2626' }}>
          {action === 'Approve' ? '✓ Approve Request' : '✕ Reject Request'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="body2" color="text.secondary" mb={2}>
            {action === 'Approve'
              ? 'Approving this will mark the task as completed.'
              : 'Please provide a reason for rejection.'}
          </Typography>
          {selected?.requestComment && (
            <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>User's Description</Typography>
              <Typography variant="body2" mt={0.5}>{selected.requestComment}</Typography>
            </Box>
          )}
          <TextField
            label={action === 'Approve' ? 'Approval note (optional)' : 'Rejection reason *'}
            value={comment} onChange={e => setComment(e.target.value)}
            fullWidth multiline rows={3}
            required={action === 'Reject'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || (action === 'Reject' && !comment.trim())}
            onClick={handleRespond}
            sx={action === 'Approve'
              ? { bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }
              : { bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
            {saving ? 'Saving…' : action === 'Approve' ? 'Confirm Approval' : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
